import React, { useState, useCallback, useEffect, useContext } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import { useApolloClient } from "react-apollo";
import ReactSelectAsync from "react-select/async";
import { Field, Control, Label, Generic, Help } from "rbx";
import { ALL_ITEMS_QUERY } from "../../graphql/Items/Items.graphql";
import { ourToast as toast } from "../../utils";
import { ModalContext } from "../ModalProvider";

const parseItemForSelect = (item, name, orderByNumber = false) => ({
  value: item.RecId,
  name,
  ItemCategory: item.ItemCategory,
  label: orderByNumber
    ? `${item.ItemNo} - ${item.Description}`
    : `${item.Description} - ${item.ItemNo}`,
  otherLabel: orderByNumber ? item.Description : item.ItemNo,
  StdCost: item.StdCost,
  AvgCost: item.AvgCost,
  listPrice: item.ListPrice
});

const ItemNumberSelect = ({
  autoFocus,
  label,
  size,
  name,
  onChange,
  disabled,
  value,
  isLoading,
  orderByNumber,
  ItemCategoryRecIdRef,
  kind,
  required,
  showHelp
}) => {
  const client = useApolloClient();
  const [selectLoading, setLoading] = useState(isLoading);
  const [defaultOptions, setDefaultOptions] = useState(
    ItemCategoryRecIdRef ? [] : null
  );
  const [lastItemCategory, setLastItemCategory] = useState(null);
  const { setModalOpen } = useContext(ModalContext);

  const loadDefaultOptions = useCallback(async () => {
    try {
      setLoading(true);
      const variables = {
        orderByMulti: orderByNumber ? ["ItemNo_ASC"] : ["Description_ASC"],
        where:
          kind === "WOParts" && typeof ItemCategoryRecIdRef !== "undefined"
            ? { ItemCatgRecIdRef: parseInt(ItemCategoryRecIdRef) }
            : {}
      };

      const itemsResult = await client.query({
        query: ALL_ITEMS_QUERY,
        variables
      });

      const items = itemsResult?.data?.Items;

      if (Array.isArray(items)) {
        const grouped = items.map(item =>
          parseItemForSelect(item, name, orderByNumber)
        );

        setDefaultOptions(grouped);
      }
    } catch (err) {
      toast.error("Error getting Items.");
    } finally {
      setLoading(false);
    }
  }, [ItemCategoryRecIdRef, client, kind, name, orderByNumber]);

  useEffect(() => {
    if (ItemCategoryRecIdRef && ItemCategoryRecIdRef !== lastItemCategory) {
      setLastItemCategory(ItemCategoryRecIdRef);
      loadDefaultOptions();
    } else {
      loadDefaultOptions();
    }
  }, [ItemCategoryRecIdRef, lastItemCategory, loadDefaultOptions]);

  const loadingOptions = useCallback(
    async (inputValue = "") =>
      new Promise(async resolve => {
        try {
          setLoading(true);
          const variables = {
            orderByMulti: orderByNumber ? ["ItemNo_ASC"] : ["Description_ASC"],
            where: {
              OR: [
                { ItemNo_contains: inputValue },
                { Description_contains: inputValue }
              ]
            }
          };

          if (ItemCategoryRecIdRef && ItemCategoryRecIdRef !== "0") {
            variables.where.ItemCatgRecIdRef = parseInt(ItemCategoryRecIdRef);
          }

          const itemsResult = await client.query({
            query: ALL_ITEMS_QUERY,
            variables,
            fetchPolicy: "cache-first"
          });

          const items = itemsResult?.data?.Items;
          if (Array.isArray(items)) {
            const grouped = itemsResult.data.Items.map(item =>
              parseItemForSelect(item, name, orderByNumber)
            );
            resolve(grouped);
          }
        } catch (err) {
          toast.error("Error getting Items.");
        } finally {
          setLoading(false);
        }
      }),
    [ItemCategoryRecIdRef, client, name, orderByNumber]
  );
  return (
    <Field>
      {showHelp && value ? (
        <Help as={Label} style={{ marginTop: 0 }}>
          <Generic
            as={Link}
            tabIndex="-1"
            textColor="link"
            to={`/inventory/items/${value?.value}/details`}
            onClick={() => setModalOpen(false)}
          >
            {label}
          </Generic>
        </Help>
      ) : (
        <Label size={size}>{label}</Label>
      )}
      <Control
        size={size}
        style={{
          border:
            required && (!value || value === "" || value === "0" || value === 0)
              ? "1px #e63362 solid"
              : "",
          borderRadius: "4px",
          boxShadow:
            required && (!value || value === "" || value === "0" || value === 0)
              ? "0 0 0 0.125em rgba(230, 51, 98, 0.25)"
              : ""
        }}
      >
        <ReactSelectAsync
          autoFocus={autoFocus}
          cacheOptions={false}
          color="danger"
          defaultOptions={defaultOptions}
          isDisabled={disabled}
          isLoading={selectLoading}
          loadOptions={loadingOptions}
          name={name}
          placeholder="Start typing..."
          styles={{
            menu: provided => ({
              ...provided,
              zIndex: 999
            })
          }}
          value={value}
          onChange={onChange}
        />
      </Control>
    </Field>
  );
};

ItemNumberSelect.propTypes = {
  autoFocus: PropTypes.bool,
  value: PropTypes.object,
  name: PropTypes.string,
  label: PropTypes.string,
  size: PropTypes.string,
  disabled: PropTypes.bool,
  onChange: PropTypes.func,
  isLoading: PropTypes.bool,
  orderByNumber: PropTypes.bool,
  ItemCategoryRecIdRef: PropTypes.number,
  kind: PropTypes.string,
  required: PropTypes.bool,
  showHelp: PropTypes.bool
};
ItemNumberSelect.defaultOptions = {
  isLoading: null,
  autoFocus: false
};
export default ItemNumberSelect;
